// fw BigClock — the elapsed clock. Spec §6: "the largest element on the
// screen. It is the number that becomes someone's pay."
//
// Renders H:MM large with seconds in a small span, condensed 700, tabular,
// in --fw-signal. Ticks once a second while `running`, and never shows a
// negative duration: a phone whose clock is behind the server's clamps to
// 0:00 rather than counting down.
//
// `now` is a test seam and a deterministic-render seam: when given, it is
// the reference time and no interval is started.
//
// ── Two ways in, one formatter (2026-09-10) ────────────────────────────────
//
// The owner: "Timer running should show seconds, so we can see timer is
// actually running." Every live clock in the app outside Field Today was a
// dead `H:MM`, and the reason none of them could simply become a BigClock is
// that this component only knew how to derive a duration from a START INSTANT.
//
// The screens that show somebody ELSE on the clock deliberately do not work
// from one. apps/shared/liveClock.js `elapsedNow` takes the server's own
// `elapsed_seconds` and adds only the time since the answer landed, so a
// device whose clock is wrong still reads the right number — which is the
// whole reason GET /api/time-entries/active/all is refused in this app (its
// raw `start_time`, read as local, made the classic badge render "-1:-1:-1").
//
// So `seconds` is a duration the caller already has, and `startedAt` is an
// instant to measure from. Whichever way the number arrives it goes through
// the same `formatClockWithSeconds`, and neither elapsed calculation is
// copied in here: a second copy of a figure that becomes pay is how two
// screens start disagreeing about one shift.
//
// A caller passing `seconds` owns the tick and gets no interval — those
// screens already run ONE for a whole list of people, and a clock that
// started its own would be N timers answering one question.

import React, { useEffect, useState } from 'react';
import { elapsedSeconds, formatClockWithSeconds } from '../format.js';
import './BigClock.css';

export const CLOCK_SIZES = ['clock', 'lg', 'md', 'sm'];

/** Is `seconds` a duration, as opposed to absent? `Number(null)` is 0. */
function givenSeconds(seconds) {
  return seconds !== undefined && seconds !== null && Number.isFinite(Number(seconds));
}

export function BigClock({ startedAt, seconds, running = true, size = 'clock', now, className = '', ...rest }) {
  const given = givenSeconds(seconds);
  const controlled = given || (now !== undefined && now !== null);
  const [tick, setTick] = useState(() => Date.now());

  useEffect(() => {
    if (controlled || !running) return undefined;
    setTick(Date.now());
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [controlled, running]);

  const reference = controlled ? now : tick;
  const value = given ? Number(seconds) : (startedAt ? elapsedSeconds(startedAt, reference) : 0);
  const { hm, s } = formatClockWithSeconds(value);
  const sz = CLOCK_SIZES.includes(size) ? size : 'clock';

  return (
    <span
      className={['fw-clock', `fw-clock--${sz}`, 'fw-num', running ? '' : 'fw-clock--paused', className].filter(Boolean).join(' ')}
      role="timer"
      aria-live="off"
      aria-label={`${hm}:${s} elapsed`}
      data-seconds={Math.max(0, Math.floor(Number(value) || 0))}
      {...rest}
    >
      <span className="fw-clock__hm">{hm}</span>
      <span className="fw-clock__s">{s}</span>
    </span>
  );
}

export default BigClock;
